/**
 * Botón "Regenerar aseo" — acción ACOTADA, visible solo a admin/superadmin.
 *
 * Caso de uso (pedido del owner, 2026-06-22): borraste por error el aseo de un
 * arriendo y quieres recrearlo desde cero, sin tener que recalcular toda la
 * reserva ni ir a la pestaña de "Aseos huérfanos".
 *
 * Llama a `POST /api/stays/regenerar-aseo/:id` (vía staysRecalcApi). El backend
 * reusa `syncCleaningFromBooking`:
 *   - si no existía aseo vinculado → lo crea (regenerado);
 *   - si existía y no está pagado → lo re-sincroniza con el arriendo;
 *   - si existía y está pagado → lo preserva sin cambios.
 * NO toca las finanzas del booking (para eso está "Recalcular finanzas").
 *
 * Se usa en bookingDetailModal.tsx (detalle de reserva desde el calendario).
 */
import { useState } from "react";
import { Button, Space, Typography, Tooltip, Modal } from "antd";
import { ClearOutlined } from "@ant-design/icons";
import { staysRecalcApi } from "../services/staysRecalc.api";
import { getMsg } from "../utils/antdStatic";
import { isAdminRole } from "../utils/roles.utils";

const { Text, Paragraph } = Typography;

interface RegenerarAseoButtonProps {
  bookingId: number;
  /** Rol del usuario actual — el botón solo se renderiza si es admin. */
  userRole: string | string[] | null | undefined;
  /** Callback tras regenerar (para que el caller refresque el calendario). */
  onRegenerated?: () => void;
  size?: "small" | "middle" | "large";
}

export default function RegenerarAseoButton({
  bookingId,
  userRole,
  onRegenerated,
  size = "small",
}: RegenerarAseoButtonProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Solo admin/superadmin. Cliente y aseador no lo ven.
  if (!isAdminRole(userRole)) return null;

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const resp = await staysRecalcApi.regenerarAseo(bookingId);
      if (!resp) {
        // apiFetch ya mostró el toast de error (ej. 409 mes cerrado).
        setLoading(false);
        return;
      }
      // Mensaje claro según lo que pasó realmente.
      getMsg()?.success(resp.message || "Aseo regenerado.", 6);
      setOpen(false);
      onRegenerated?.();
    } finally {
      setLoading(false);
    }
  };

  const tooltipTitle = (
    <span>
      Recrea el aseo de limpieza vinculado a esta reserva si fue borrado por
      error. No modifica las finanzas del arriendo ni los demás satélites.
    </span>
  );

  return (
    <>
      <Tooltip title={tooltipTitle}>
        <Button
          size={size}
          icon={<ClearOutlined />}
          onClick={() => setOpen(true)}
        >
          Regenerar aseo
        </Button>
      </Tooltip>
      <Modal
        open={open}
        title={
          <Space>
            <ClearOutlined />
            <span>Regenerar el aseo de esta reserva</span>
          </Space>
        }
        okText={loading ? "Regenerando…" : "Sí, regenerar aseo"}
        cancelText="Cancelar"
        confirmLoading={loading}
        onOk={handleConfirm}
        onCancel={() => {
          if (!loading) setOpen(false);
        }}
        maskClosable={!loading}
        width={560}
      >
        <Paragraph>
          Esta acción <Text strong>vuelve a crear el aseo de limpieza</Text>{" "}
          vinculado a este arriendo, usando los datos actuales de la propiedad y
          la reserva.
        </Paragraph>
        <Paragraph type="secondary" style={{ fontSize: 13 }}>
          <Text strong>¿Cuándo usarla?</Text> Si borraste el aseo de este
          arriendo por error y lo necesitas de vuelta. Ejemplo: eliminaste el
          aseo desde la agenda y la reserva quedó sin su limpieza asociada.
        </Paragraph>
        <ul style={{ marginTop: 0, marginBottom: 12, fontSize: 13 }}>
          <li>
            Si el aseo <Text strong>no existe</Text> → se crea de nuevo y queda
            vinculado al arriendo.
          </li>
          <li>
            Si el aseo <Text strong>ya existe</Text> → se re-sincroniza con la
            reserva (no se duplica: hay máximo 1 aseo por arriendo).
          </li>
          <li>
            Si el aseo <Text strong>ya está pagado</Text> → se preserva tal cual
            (no se pisa una liquidación hecha).
          </li>
        </ul>
        <Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 0 }}>
          <Text strong>No</Text> modifica las finanzas del arriendo (para eso
          está "Recalcular finanzas"), ni el bloqueo de salida tardía ni la
          lavandería. Si el mes ya está cerrado, la acción se bloquea.
        </Paragraph>
      </Modal>
    </>
  );
}
